import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterBar from '@/components/feature/FilterBar';
import MovieCard from '@/components/feature/MovieCard';
import SearchBox from '@/components/feature/SearchBox';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getHotMovies, queryMovies, type DecadeFilter, type SortFilter } from '@/utils/movies';

const hotMovies = getHotMovies(6);

export default function Search() {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';

  const [genre, setGenre] = useState('all');
  const [decade, setDecade] = useState<DecadeFilter>('all');
  const [sort, setSort] = useState<SortFilter>('latest');

  useEffect(() => {
    setGenre('all');
    setDecade('all');
    setSort('latest');
  }, [keyword]);

  usePageMeta({
    title: keyword ? `“${keyword}” 搜索结果 - 人间中毒在线` : '搜索电影 - 人间中毒在线',
    description: keyword
      ? `人间中毒在线搜索结果：与“${keyword}”相关的电影内容，包含剧情介绍与观影推荐。`
      : '在人间中毒在线搜索你感兴趣的电影，支持按片名、主演与类型进行关键词查找。',
    keywords: `人间中毒在线,电影搜索,${keyword || '影视资源'},剧情介绍`,
  });

  const results = useMemo(
    () => queryMovies({ keyword, genre, decade, sort }),
    [keyword, genre, decade, sort],
  );

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />
            <h1 className="text-2xl font-extrabold text-foreground-950 md:text-3xl">
              {keyword ? (
                <>
                  搜索：<span className="text-accent-700">“{keyword}”</span>
                </>
              ) : (
                '搜索电影'
              )}
            </h1>
            <div className="mt-5 max-w-2xl">
              <SearchBox size="lg" defaultValue={keyword} />
            </div>
            <p className="mt-4 text-sm text-foreground-600">
              共找到 <span className="font-semibold text-accent-700">{results.length}</span> 部相关影片
              {keyword && <span className="ml-2 text-foreground-500">（匹配片名、主演、导演与类型）</span>}
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <FilterBar
            genre={genre}
            decade={decade}
            sort={sort}
            onGenreChange={setGenre}
            onDecadeChange={setDecade}
            onSortChange={setSort}
          />

          {results.length > 0 ? (
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {results.map((movie, i) => (
                <div key={movie.id} className={`anim-fade-up delay-${(i % 5) + 1}`}>
                  <MovieCard movie={movie} />
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-6">
              <div className="flex flex-col items-center rounded-lg border border-background-200 bg-background-100 px-6 py-14 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-3xl"></i>
                </span>
                <h2 className="mt-5 text-lg font-bold text-foreground-950">
                  {keyword ? `没有找到与“${keyword}”相关的影片` : '输入关键词开始搜索'}
                </h2>
                <p className="mt-2 max-w-md text-sm text-foreground-600">
                  可以尝试更换或精简关键词，例如搜索片名、主演名字或电影类型（如「科幻」「悬疑」）。
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {['动作', '剧情', '爱情', '科幻', '悬疑'].map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="rounded-full border border-background-200 bg-background-50 px-4 py-1.5 text-sm text-foreground-700 transition hover:border-accent-400 hover:text-accent-700"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-10">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950">
                  <i className="ri-fire-line text-accent-500"></i>
                  为你推荐热门影片
                </h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                  {hotMovies.map((movie, i) => (
                    <div key={movie.id} className={`anim-fade-up delay-${(i % 5) + 1}`}>
                      <MovieCard movie={movie} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}